import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import {
  getUserSettingsCulture,
  getUserSettingsCurrency,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { LocalizationService } from '@capital-access/common/localization';
import { NotificationService } from '@capital-access/firefly/components';
import { createBnyManagementReport, createBnyManagementReportFinished } from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { generateReport } from '../../core/+state/report.actions';
import { OwnershipSource } from '../../core/models/ownership-source.enum';
import { BnyManagementReportRequest } from '../models/bny-management-report.model';

@Injectable()
export class BnyManagementReportEffects {
  createReportEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createBnyManagementReport),
      withLatestFrom(
        this.store.select(getUserSettingsCulture),
        this.store.select(getUserSettingsLanguage),
        this.store.select(getUserSettingsCurrency),
        this.store.select(getUserSettingsTimezone)
      ),
      mergeMap(([request, culture, language, currency, timeZone]) => {
        const reportRequest: BnyManagementReportRequest = {
          ...request,
          culture,
          language,
          currency,
          pullSampleData: false,
          fileName: '',
          timeZone,
          dataSource: OwnershipSource.Public
        };

        const generatedRequest = this.generateReportRequest(reportRequest);
        return this.localization.localize('exportStarted', {}, { scope: LOCALIZATION_KEY }).pipe(
          tap(message => this.notificationService.notify(message, { type: 'info' })),
          switchMap(() => of(generatedRequest))
        );
      })
    )
  );

  private generateReportRequest(request: BnyManagementReportRequest) {
    const successMessage = `bnyManagementReportCreated`;
    const failureMessage = `bnyManagementReportFailed`;

    return generateReport({
      request,
      url: 'bny-management-reports/process/launch',
      successMessage,
      failureMessage,
      finishAction: createBnyManagementReportFinished()
    });
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private localization: LocalizationService,
    private notificationService: NotificationService
  ) {}
}
